import { z } from 'zod';
import { canonicalEmoji, isSingleEmoji } from '../emoji.js';
import { MESSAGE_TYPES, ALBUM_LIMITS, POLL_LIMITS, GATHERING_LIMITS, IDEA_LIMITS, IDEA_STATUSES, USER_KINDS, CONVERSATION_TYPES, WS_EVENTS, FAMILY_ROLES, ENROLLMENT_TOKEN_KINDS, ENROLLMENT_TOKEN_STATUS, FAMILY_INVITE_STATUS, FAMILY_LIMITS, CHAT_LIMITS, FAMILY_APP_LIMITS, PUSH_LIMITS, CALL_LIMITS, LIST_REPEATS, CALL_TYPES, ENROLLMENT_LIMITS, RESERVED_HANDLES, RESERVED_FAMILY_HANDLES, FAMILY_HANDLE_LIMITS, FILE_LIMITS, MESSAGE_LIMITS, SCHEDULE_LIMITS, JOIN_LIMITS, QR_DEVICE_KINDS, APP_MODES, REPORT_REASONS, REPORT_LIMITS, } from '../constants.js';
// ============================================
// Base Schemas
// ============================================
export const idSchema = z.string().uuid();
export const timestampSchema = z.string().datetime();
export const paginationSchema = z.object({
    page: z.number().int().min(1).default(1),
    limit: z.number().int().min(1).max(100).default(20),
    total: z.number().int().min(0).optional(),
    hasMore: z.boolean().optional(),
});
export const handleRegex = /^[a-z0-9_]+$/;
export const handleSchema = z
    .string()
    .min(FAMILY_LIMITS.MIN_HANDLE_LENGTH)
    .max(FAMILY_LIMITS.MAX_HANDLE_LENGTH)
    .regex(handleRegex, 'Handle must be lowercase letters, numbers, and underscores only')
    .refine((h) => !RESERVED_HANDLES.includes(h), {
    message: 'This handle is reserved',
});
// Family handles (M59, D215): `[a-z0-9-]`, 3–32, no dash at either end and
// no two in a row, not reserved.
export const familyHandleRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** What people type: spaces and a leading @ dropped, lower-cased. */
export const normalizeFamilyHandle = (raw) => raw.trim().replace(/^@+/, '').toLowerCase();
/** Why a (normalized) family handle can't be used, or null when it can. */
export function familyHandleProblem(handle) {
    if (handle.length < FAMILY_HANDLE_LIMITS.MIN_LENGTH ||
        handle.length > FAMILY_HANDLE_LIMITS.MAX_LENGTH ||
        !familyHandleRegex.test(handle))
        return 'invalid';
    if (RESERVED_FAMILY_HANDLES.includes(handle))
        return 'reserved';
    return null;
}
export const familyHandleSchema = z
    .string()
    .min(FAMILY_HANDLE_LIMITS.MIN_LENGTH)
    .max(FAMILY_HANDLE_LIMITS.MAX_LENGTH)
    .regex(familyHandleRegex, 'Use small letters, numbers and single dashes (not at the start or end)')
    .refine((h) => !RESERVED_FAMILY_HANDLES.includes(h), {
    message: 'This handle is reserved',
});
/** Changing a family's handle (D216); typed input is normalized first. */
export const updateFamilyHandleSchema = z.object({
    handle: z
        .string()
        .max(100)
        .transform(normalizeFamilyHandle)
        .pipe(familyHandleSchema),
});
// ============================================
// User Schemas
// ============================================
export const userSchema = z.object({
    id: idSchema,
    kind: z.enum([USER_KINDS.HUMAN, USER_KINDS.APP]),
    handle: handleSchema,
    displayName: z.string().min(1).max(100),
    avatarUrl: z.string().nullable(),
    publicKey: z.string().nullable(), // For future E2EE
    /** Day and month of their birthday, if they added it (D144). */
    birthDay: z.number().int().nullable().optional(),
    birthMonth: z.number().int().nullable().optional(),
    lastSeenAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
// Days in each month, with February's 29 allowed: a birthday has no year (D144).
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export function isValidBirthday(day, month) {
    return (Number.isInteger(day) &&
        Number.isInteger(month) &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= DAYS_IN_MONTH[month - 1]);
}
// A birthday, day and month only (D144).
export const birthdaySchema = z
    .object({
    day: z.number().int().min(1).max(31),
    month: z.number().int().min(1).max(12),
})
    .refine((value) => isValidBirthday(value.day, value.month), {
    message: 'That day is not in that month',
    path: ['day'],
});
export const updateUserSchema = z.object({
    displayName: z.string().min(1).max(100).optional(),
    // A picture as a data: URL, checked on the server; null removes it. Not a
    // free URL: that could point anywhere (issue #24).
    avatarBase64: z
        .string()
        .max(FAMILY_LIMITS.MAX_AVATAR_BASE64_LENGTH)
        .nullable()
        .optional(),
    publicKey: z.string().nullable().optional(),
    /** Set with { day, month }, cleared with null (D144). */
    birthday: birthdaySchema.nullable().optional(),
    // A sealed picture (D292): base64 of the sealed record, with its key
    // wrapped as a `pkey` family-app record for each family wrapped now. null
    // removes it. `avatarBase64` above is refused (D297).
    picture: z
        .object({
        sealed: z
            .string()
            .min(1)
            .max(FAMILY_LIMITS.MAX_SEALED_PICTURE_LENGTH)
            .regex(/^[A-Za-z0-9+/]+={0,2}$/),
        keys: z
            .array(z.object({
            familyId: z.string().uuid(),
            sealed: z.string().max(200_000),
        }))
            .max(100),
    })
        .nullable()
        .optional(),
});
// Wrap (or re-wrap) my current picture's key for one family (D295).
export const putPictureKeySchema = z.object({
    pictureHash: z.string().regex(/^[0-9a-f]{32}$/),
    // Checked as a `pkey` record of that family by the route.
    sealed: z.string().max(200_000),
});
// ============================================
// Auth Schemas
// ============================================
export const authTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
});
export const refreshTokenSchema = z.object({
    refreshToken: z.string(),
});
// System-root admin panel login (identity spec §6.2 — env-var credentials)
export const adminLoginSchema = z.object({
    username: z.string().min(1),
    password: z.string().min(1),
});
// ============================================
// Enrollment Token Schemas (one-time QR registration)
// ============================================
export const familyRoleSchema = z.enum([
    FAMILY_ROLES.ROOT,
    FAMILY_ROLES.ADMIN,
    FAMILY_ROLES.MEMBER,
]);
export const enrollmentTokenSchema = z.object({
    id: idSchema,
    kind: z.enum([
        ENROLLMENT_TOKEN_KINDS.ENROLL,
        ENROLLMENT_TOKEN_KINDS.ADD_DEVICE,
    ]),
    familyId: idSchema,
    handle: handleSchema.nullable(),
    displayName: z.string().min(1).max(100).nullable(),
    familyRole: familyRoleSchema,
    userId: idSchema.nullable(),
    status: z.enum([
        ENROLLMENT_TOKEN_STATUS.PENDING,
        ENROLLMENT_TOKEN_STATUS.CONSUMED,
        ENROLLMENT_TOKEN_STATUS.REVOKED,
        ENROLLMENT_TOKEN_STATUS.EXPIRED,
    ]),
    invitedBy: idSchema.nullable(),
    createdBySystemRoot: z.boolean(),
    expiresAt: timestampSchema,
    consumedAt: timestampSchema.nullable(),
    consumedBy: idSchema.nullable(),
    createdAt: timestampSchema,
});
// A one-time personal link (kind='enroll'): it makes a NEW account, or lets
// someone already on Twinden join with their own passkey (D151). Nobody
// makes an add_device link for someone else: only the person adds a device
// to their own account, with Add a device (D90, D334, #105).
//
// The link holds no handle (D348, owner 2026-10-30): someone already on
// Twinden joins with their own, and a new person picks theirs on the link
// page. A `handle` sent by an older app is dropped, not reserved.
export const createEnrollmentTokenSchema = z.object({
    kind: z.literal(ENROLLMENT_TOKEN_KINDS.ENROLL),
    displayName: z.string().min(1).max(100),
    familyRole: familyRoleSchema.default(FAMILY_ROLES.MEMBER),
    expiresInDays: z
        .number()
        .int()
        .min(1)
        .max(ENROLLMENT_LIMITS.MAX_EXPIRY_DAYS)
        .default(ENROLLMENT_LIMITS.DEFAULT_EXPIRY_DAYS),
});
// Public pre-enrollment info returned by GET /api/enroll/:token
export const enrollmentInfoSchema = z.object({
    kind: z.enum([
        ENROLLMENT_TOKEN_KINDS.ENROLL,
        ENROLLMENT_TOKEN_KINDS.ADD_DEVICE,
    ]),
    familyName: z.string(),
    handle: handleSchema.nullable(),
    displayName: z.string().nullable(),
});
// ============================================
// Family Invite Schemas (existing user → additional family)
// ============================================
export const familyInviteSchema = z.object({
    id: idSchema,
    familyId: idSchema,
    targetUserId: idSchema,
    familyRole: familyRoleSchema,
    invitedBy: idSchema,
    status: z.enum([
        FAMILY_INVITE_STATUS.PENDING,
        FAMILY_INVITE_STATUS.ACCEPTED,
        FAMILY_INVITE_STATUS.DECLINED,
        FAMILY_INVITE_STATUS.REVOKED,
        FAMILY_INVITE_STATUS.EXPIRED,
    ]),
    createdAt: timestampSchema,
    respondedAt: timestampSchema.nullable(),
    expiresAt: timestampSchema,
});
// "Add someone you know" (D350): a person who shares another family with
// the inviter, by id, never by a typed handle. Only roots offer admin or
// root (D1); the server checks every rule.
export const createFamilyInviteSchema = z.object({
    userId: idSchema,
    familyRole: familyRoleSchema.default(FAMILY_ROLES.MEMBER),
});
// A new person on a personal link that holds no handle (D348): one of the
// link's own suggestions with its token; left out, the server picks one.
export const personalLinkHandleSchema = z.object({
    handle: handleSchema.optional(),
    handleToken: z.string().max(200).optional(),
});
// ============================================
// Device (passkey credential) Schemas
// ============================================
export const deviceSchema = z.object({
    id: idSchema,
    deviceName: z.string().max(100).nullable(),
    transports: z.array(z.string()).nullable(),
    createdAt: timestampSchema,
    lastUsedAt: timestampSchema.nullable(),
});
export const updateDeviceSchema = z.object({
    deviceName: z.string().min(1).max(100),
});
// POST /users/me/app-mode (M53, D186).
export const appModeSchema = z.object({ mode: z.enum(APP_MODES) });
// Signed-in sessions (D213). Signing out another session needs a fresh
// passkey check (D91); the current one doesn't (it's an ordinary sign-out).
export const revokeSessionSchema = z.object({
    verification: z.record(z.unknown()).optional(),
});
// DELETE /users/me/sessions: "Sign out everywhere else".
export const revokeOtherSessionsSchema = z.object({
    exceptCurrent: z.literal(true),
    verification: z.record(z.unknown()).optional(),
});
// ============================================
// Message Schemas
// ============================================
export const messageSchema = z.object({
    id: idSchema,
    conversationId: idSchema,
    senderId: idSchema,
    content: z.string(), // Encrypted content (future E2EE)
    type: z.enum([
        MESSAGE_TYPES.TEXT,
        MESSAGE_TYPES.IMAGE,
        MESSAGE_TYPES.FILE,
        MESSAGE_TYPES.VOICE,
        MESSAGE_TYPES.SYSTEM,
        MESSAGE_TYPES.ALBUM,
        MESSAGE_TYPES.POLL,
        MESSAGE_TYPES.GATHERING,
    ]),
    metadata: z
        .object({
        fileName: z.string().optional(),
        fileSize: z.number().optional(),
        mimeType: z.string().optional(),
        duration: z.number().optional(), // For voice messages
        waveform: z.array(z.number()).optional(), // Voice note bar heights 0-100
        width: z.number().optional(), // For images
        height: z.number().optional(),
    })
        .nullable(),
    readBy: z.array(idSchema),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
// End-to-end encryption (M28, docs/end-to-end-encryption.md)
// 'v1': associated data binds the chat and client id; 'v2' (M76, D324–D329)
// also binds the sender, type, epoch and revision. The server can't tell
// them apart and doesn't try: it stores the label and passes it on.
export const ENVELOPE_VERSIONS = ['v1', 'v2'];
export const envelopeVersionSchema = z.enum(ENVELOPE_VERSIONS);
/** A stored label as a version, or null (plaintext, or anything unknown). */
export const envelopeVersion = (value) => (value === 'v1' || value === 'v2' ? value : null);
const base64Schema = (max) => z
    .string()
    .min(1)
    .max(max)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/);
/** `PUT /users/me/public-key` (the pre-M28 key field): validated, not destructured (M67 review). */
export const updatePublicKeySchema = z.object({
    publicKey: z.string().min(1).max(10_000),
});
/** `POST /messages/read` (M67 review): ids only, a bounded list. */
export const markReadSchema = z.object({
    conversationId: idSchema,
    messageIds: z.array(idSchema).min(1).max(1000),
});
/**
 * An ML-KEM-768 public key (M78, D336): base64 of the 1184-byte `ek`, 1580
 * characters. The server checks only the length; devices run FIPS 203's
 * key check before using one.
 */
export const mlkemPublicKeySchema = z
    .string()
    .length(1580)
    .regex(/^[A-Za-z0-9+/]+=$/);
export const registerDeviceKeySchema = z.object({
    publicKey: base64Schema(400),
    label: z.string().trim().max(100).optional(),
    // M78: optional, an app from before M78 sends none.
    mlkemPublicKey: mlkemPublicKeySchema.optional(),
});
/** `PUT /keys/devices/:id/mlkem` (M78, docs/post-quantum.md §4.2). */
export const setMlkemKeySchema = z.object({
    mlkemPublicKey: mlkemPublicKeySchema,
});
// A v2 copy is 1532 base64 characters (D337); 1600 leaves room.
const wrappedKeysSchema = z
    .array(z.object({ deviceKeyId: idSchema, wrappedKey: base64Schema(1600) }))
    .min(1)
    .max(500);
export const keyEpochSchema = z.object({
    epoch: z.number().int().min(1),
    wrappedByDeviceId: idSchema,
    keys: wrappedKeysSchema,
});
// M71 (D282): plain gathering settings on the message.
export const gatheringSettingsSchema = z.object({
    startsAt: z.string().datetime({ offset: true }),
    rings: z.boolean(),
});
export const gatheringReplySchema = z
    .object({
    reply: z.enum(['coming', 'late', 'not']),
    lateMinutes: z
        .number()
        .refine((n) => GATHERING_LIMITS.LATE_MINUTES.includes(n))
        .optional(),
})
    .refine((v) => (v.reply === 'late') === (v.lateMinutes !== undefined), {
    message: 'lateMinutes goes with late, and only with late',
});
/** What a gathering's sealed content holds (D282), checked on the device. */
export const gatheringContentSchema = z.object({
    title: z.string().trim().min(1).max(GATHERING_LIMITS.TITLE_LENGTH),
    place: z.string().trim().max(GATHERING_LIMITS.PLACE_LENGTH).optional(),
    note: z.string().trim().max(GATHERING_LIMITS.NOTE_LENGTH).optional(),
});
// M60 (D220): plain poll settings on the message.
export const pollSettingsSchema = z.object({
    options: z
        .number()
        .int()
        .min(POLL_LIMITS.MIN_OPTIONS)
        .max(POLL_LIMITS.MAX_OPTIONS),
    multiple: z.boolean(),
    // Relative, so a poll that waited in the outbox still gets its full time:
    // the server sets the close time when the poll arrives (D220).
    closesInSeconds: z
        .number()
        .int()
        .min(60)
        .max(POLL_LIMITS.MAX_OPEN_MS / 1000)
        .nullable()
        .optional(),
    // An absolute time, as the first M60 web build sent it.
    closesAt: z.string().datetime({ offset: true }).nullable().optional(),
});
// The sender's own list of who's @mentioned in an encrypted message, which
// the server can't read (D61).
const declaredMentionsSchema = z.array(idSchema).max(100);
export const sendMessageSchema = z.object({
    conversationId: idSchema,
    content: z.string().min(1),
    // Quoted reply (M20, D20); must be in the same conversation.
    replyToId: idSchema.optional(),
    // M23 (D32): makes a retried send idempotent.
    clientId: idSchema.optional(),
    // M28: content is an encrypted envelope under this chat key epoch.
    encryption: envelopeVersionSchema.optional(),
    keyEpoch: z.number().int().min(1).optional(),
    mentions: declaredMentionsSchema.optional(),
    mentionsEveryone: z.boolean().optional(),
    type: z
        .enum([
        MESSAGE_TYPES.TEXT,
        MESSAGE_TYPES.IMAGE,
        MESSAGE_TYPES.GIF,
        MESSAGE_TYPES.VIDEO,
        MESSAGE_TYPES.FILE,
        MESSAGE_TYPES.VOICE,
        MESSAGE_TYPES.ALBUM,
        MESSAGE_TYPES.POLL,
        MESSAGE_TYPES.GATHERING,
    ])
        .default(MESSAGE_TYPES.TEXT),
    metadata: z
        .object({
        fileName: z.string().optional(),
        fileSize: z.number().optional(),
        mimeType: z.string().optional(),
        duration: z.number().min(0).optional(),
        waveform: z
            .array(z.number().int().min(0).max(100))
            .max(FILE_LIMITS.VOICE_WAVEFORM_BARS)
            .optional(),
        width: z.number().int().positive().optional(),
        height: z.number().int().positive().optional(),
        thumbnailUrl: z.string().optional(),
        // M28: uploaded (encrypted) files an encrypted message refers to.
        // An album (M60) lists each item's file and preview.
        files: z
            .array(z.string().max(500))
            .max(ALBUM_LIMITS.MAX_FILES)
            .optional(),
        // M60 (D219): how many photos and videos an album holds; counts only.
        album: z
            .object({
            photos: z.number().int().min(0).max(ALBUM_LIMITS.MAX_ITEMS),
            videos: z.number().int().min(0).max(ALBUM_LIMITS.MAX_ITEMS),
        })
            .refine(({ photos, videos }) => photos + videos >= ALBUM_LIMITS.MIN_ITEMS &&
            photos + videos <= ALBUM_LIMITS.MAX_ITEMS, 'An album holds 2-10 items')
            .optional(),
        // M60 (D220): a poll's settings, which the server needs to check votes.
        // Its question and options are sealed in the content.
        poll: pollSettingsSchema.optional(),
        // M71 (D282): when the gathering starts and whether it may ring. Its
        // title, place and note are sealed in the content.
        gathering: gatheringSettingsSchema.optional(),
    })
        .optional(),
});
// Voting replaces your previous choice (D220).
export const pollVoteSchema = z.object({
    optionIndexes: z
        .array(z
        .number()
        .int()
        .min(0)
        .max(POLL_LIMITS.MAX_OPTIONS - 1))
        .min(1)
        .max(POLL_LIMITS.MAX_OPTIONS)
        .refine((list) => new Set(list).size === list.length, 'Duplicate option'),
});
/** What a poll's sealed content holds (D220), checked on the device. */
export const pollContentSchema = z.object({
    question: z.string().trim().min(1).max(POLL_LIMITS.QUESTION_LENGTH),
    options: z
        .array(z.string().trim().min(1).max(POLL_LIMITS.OPTION_LENGTH))
        .min(POLL_LIMITS.MIN_OPTIONS)
        .max(POLL_LIMITS.MAX_OPTIONS),
});
// M20 (docs/chat-essentials.md)
export const editMessageSchema = z.object({
    content: z.string().trim().min(1),
    // M28
    encryption: envelopeVersionSchema.optional(),
    keyEpoch: z.number().int().min(1).optional(),
    // M76 (D326): the revision a v2 edit was sealed for; above the stored one.
    revision: z.number().int().min(1).max(2_147_483_647).optional(),
    mentions: declaredMentionsSchema.optional(),
    mentionsEveryone: z.boolean().optional(),
});
// A single emoji (with modifiers/ZWJ sequences, keycaps, ℹ️), no text (D21).
// Stored in one spelling, so 👍 and 👍️ are one reaction (M69, D264).
export const reactionSchema = z.object({
    emoji: z
        .string()
        .min(1)
        .max(32)
        .transform(canonicalEmoji)
        .refine(isSingleEmoji, 'Must be a single emoji'),
    /**
     * Set this reaction rather than toggle it (M29, D69), so a replayed
     * reaction can't undo itself. Without it the same emoji again removes it.
     */
    set: z.boolean().optional(),
});
// Forward to up to FORWARD_MAX_TARGETS chats (M22, D29).
export const forwardMessageSchema = z.object({
    conversationIds: z
        .array(idSchema)
        .min(1)
        .max(MESSAGE_LIMITS.FORWARD_MAX_TARGETS)
        .refine((ids) => new Set(ids).size === ids.length, 'Duplicate chats'),
    // M28 (D61): a copy sealed for each end-to-end encrypted target.
    encrypted: z
        .array(z.object({
        conversationId: idSchema,
        content: z.string().min(1),
        keyEpoch: z.number().int().min(1),
        clientId: idSchema,
        // M76: absent = v1, as older apps send it.
        encryption: envelopeVersionSchema.optional(),
    }))
        .max(MESSAGE_LIMITS.FORWARD_MAX_TARGETS)
        .optional(),
});
export const messageSearchSchema = z.object({
    q: z
        .string()
        .trim()
        .min(MESSAGE_LIMITS.SEARCH_MIN_LENGTH)
        .max(MESSAGE_LIMITS.SEARCH_MAX_LENGTH),
});
// ============================================
// Conversation Schemas
// ============================================
export const conversationSchema = z.object({
    id: idSchema,
    familyId: idSchema.nullable(), // NOT NULL at DB level once Phase 6 lands
    type: z.enum([CONVERSATION_TYPES.DIRECT, CONVERSATION_TYPES.GROUP]),
    name: z.string().nullable(), // Only for groups
    avatarUrl: z.string().nullable(),
    participantIds: z.array(idSchema),
    createdById: idSchema,
    lastMessageAt: timestampSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const createConversationSchema = z.object({
    familyId: idSchema,
    type: z.enum([CONVERSATION_TYPES.DIRECT, CONVERSATION_TYPES.GROUP]),
    name: z.string().min(1).max(100).optional(),
    participantIds: z.array(idSchema).min(1),
});
// Family-scoped conversation creation (D11, §8). The family id comes from the
// path; DMs are find-or-create by participant pair.
export const createFamilyConversationSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(CONVERSATION_TYPES.GROUP),
        name: z.string().min(1).max(100),
        participantIds: z.array(idSchema).min(1).max(100),
    }),
    z.object({
        type: z.literal(CONVERSATION_TYPES.DIRECT),
        userId: idSchema,
    }),
]);
// A group's description, sealed on the device with the chat's key like a
// text message (D234). The server stores it and can't read it.
export const sealedDescriptionSchema = z.object({
    content: z.string().min(1).max(CHAT_LIMITS.SEALED_DESCRIPTION_MAX),
    keyEpoch: z.number().int().positive(),
    clientId: idSchema,
    // M76 (D325): v2 is bound in the group domain; absent = v1.
    encryption: envelopeVersionSchema.optional(),
    // M76 (D326): the revision a v2 description was sealed for.
    revision: z.number().int().min(1).max(2_147_483_647).optional(),
});
export const updateConversationSchema = z.object({
    name: z.string().trim().min(1).max(100).optional(),
    // D234: sealed, or null to clear.
    description: sealedDescriptionSchema.nullable().optional(),
    // A picture as a checked data: URL, like families and people; never a
    // free URL, which could point anywhere (issue #24). null removes it.
    avatarBase64: z
        .string()
        .max(FAMILY_LIMITS.MAX_AVATAR_BASE64_LENGTH)
        .nullable()
        .optional(),
    // A sealed picture, sealed with the chat's key like the description
    // (D291, D234); null removes it. `avatarBase64` is refused (D297).
    picture: z
        .object({
        content: z.string().min(1).max(FAMILY_LIMITS.MAX_SEALED_PICTURE_LENGTH),
        keyEpoch: z.number().int().positive(),
        clientId: idSchema,
        // Optional: a device that names the chat it sealed for lets the server
        // refuse one sealed for another.
        conversationId: idSchema.optional(),
        // M76 (D325): v2 is bound in the group domain; absent = v1.
        encryption: envelopeVersionSchema.optional(),
        // M76 (D326): the revision a v2 picture was sealed for.
        revision: z.number().int().min(1).max(2_147_483_647).optional(),
    })
        .nullable()
        .optional(),
});
// My own settings for a chat (M63, D231–D233).
export const chatSettingsSchema = z
    .object({
    archived: z.boolean().optional(),
    keepArchived: z.boolean().optional(),
    // false = mark read: clears the mark and reads everything (D232).
    markedUnread: z.boolean().optional(),
    mute: z.enum(['8h', '1w', 'always']).nullable().optional(),
    // M64 (D237): pinned to the top of the home.
    pinned: z.boolean().optional(),
})
    .strict();
// Disappearing messages for a chat (M64, D238).
export const disappearingSchema = z
    .object({ duration: z.enum(['off', '24h', '7d', '90d']) })
    .strict();
// A message to send later (M64, D239): text, sealed on the device like a
// send, with when it should go.
export const scheduleMessageSchema = z
    .object({
    clientId: idSchema,
    content: z.string().min(1).max(SCHEDULE_LIMITS.CONTENT_MAX),
    encryption: envelopeVersionSchema.optional(),
    keyEpoch: z.number().int().min(1).optional(),
    mentions: z.array(idSchema).max(100).optional(),
    mentionsEveryone: z.boolean().optional(),
    replyToId: idSchema.optional(),
    scheduledFor: z.string().datetime({ offset: true }),
})
    .strict();
// The same scheduled message sealed again under the chat's current key
// (D239), after a rotation.
export const resealScheduledSchema = z
    .object({
    content: z.string().min(1).max(SCHEDULE_LIMITS.CONTENT_MAX),
    encryption: envelopeVersionSchema.optional(),
    keyEpoch: z.number().int().min(1).optional(),
})
    .strict();
// ============================================
// WebSocket Schemas
// ============================================
const wsEventValues = Object.values(WS_EVENTS);
export const wsMessageSchema = z.object({
    event: z.enum(wsEventValues),
    payload: z.unknown(),
    timestamp: timestampSchema,
});
export const wsTypingSchema = z.object({
    conversationId: idSchema,
    isTyping: z.boolean(),
});
// ============================================
// API Response Schemas
// ============================================
export function apiResponseSchema(dataSchema) {
    return z.object({
        success: z.boolean(),
        data: dataSchema.optional(),
        error: z
            .object({
            code: z.string(),
            message: z.string(),
            details: z.unknown().optional(),
        })
            .optional(),
        pagination: paginationSchema.optional(),
    });
}
export const apiErrorSchema = z.object({
    success: z.literal(false),
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
// ============================================
// Family Schemas
// ============================================
export const familySchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(FAMILY_LIMITS.MAX_NAME_LENGTH),
    description: z.string().max(FAMILY_LIMITS.MAX_DESCRIPTION_LENGTH).nullable(),
    avatarUrl: z.string().nullable(),
    createdById: idSchema.nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const createFamilySchema = z.object({
    name: z.string().min(1).max(FAMILY_LIMITS.MAX_NAME_LENGTH),
    description: z.string().max(FAMILY_LIMITS.MAX_DESCRIPTION_LENGTH).optional(),
    avatarBase64: z
        .string()
        .max(FAMILY_LIMITS.MAX_AVATAR_BASE64_LENGTH)
        .optional(),
});
// Delete family (M54, D188): a fresh passkey check and the family's name
// typed exactly. The name is compared after trimming (familyNameMatches).
export const deleteFamilySchema = z.object({
    verification: z.record(z.unknown()).optional(),
    name: z.string().max(200),
});
/**
 * The typed name confirms only if it is exactly the family's (D188). Both
 * are NFC-normalised first: Hindi (and other Indic) keyboards can produce
 * the same letters as different code points (नि as न + ि, or a nukta
 * letter as one code point or two).
 */
export function familyNameMatches(typed, familyName) {
    return typed.normalize('NFC').trim() === familyName.normalize('NFC').trim();
}
export const updateFamilySchema = z.object({
    name: z.string().min(1).max(FAMILY_LIMITS.MAX_NAME_LENGTH).optional(),
    description: z
        .string()
        .max(FAMILY_LIMITS.MAX_DESCRIPTION_LENGTH)
        .nullable()
        .optional(),
    avatarBase64: z
        .string()
        .max(FAMILY_LIMITS.MAX_AVATAR_BASE64_LENGTH)
        .nullable()
        .optional(),
    // A `picture` family-app record (D291); null removes it. `avatarBase64` is
    // refused (D297).
    pictureSealed: z
        .string()
        .max(FAMILY_LIMITS.MAX_SEALED_PICTURE_LENGTH)
        .nullable()
        .optional(),
});
// ============================================
// Family Member Schemas
// ============================================
export const familyMemberSchema = z.object({
    id: idSchema,
    familyId: idSchema,
    userId: idSchema,
    role: familyRoleSchema,
    nickname: z.string().max(50).nullable(),
    joinedAt: timestampSchema,
});
export const updateFamilyMemberSchema = z.object({
    role: familyRoleSchema.optional(),
    nickname: z.string().max(50).nullable().optional(),
});
// ============================================
// Family Apps Schemas (stars, links, documents, lists)
// ============================================
// Stars are personal and silent (D14) — no request body, no broadcast.
export const starMessageSchema = z.object({});
// Link preview lookups take one URL; the route rejects non-http(s) schemes.
export const linkPreviewQuerySchema = z.object({
    url: z.string().min(1).max(2048),
});
// POST /api/link-preview (#80): the link travels in the body, so it never
// appears in a request URL or the request log. `ephemeral`: not kept in the
// preview cache (sealed messages and records, D61).
export const linkPreviewRequestSchema = linkPreviewQuerySchema.extend({
    ephemeral: z.boolean().optional(),
    // D318: also fetch the page's picture, returned as bytes for the sender's
    // device to shrink and seal. Never cached, never kept.
    image: z.boolean().optional(),
});
// A link can be added by URL or promoted from a chat message.
// M28 part 2 (D65): an encrypted family-app record,
// `e2ee:v1:<familyId>:<epoch>:<kind>:<ciphertext>`; since M76 `e2ee:v2:…`,
// bound to the record's id too (D329). Same groups either way.
export const SEALED_PATTERN = /^e2ee:v[12]:([0-9a-f-]{36}):(\d+):([a-z]+):([A-Za-z0-9+/]+={0,2})$/;
export const sealedSchema = z.string().max(200_000).regex(SEALED_PATTERN);
// M76 (D329): a record's id, chosen by the device that seals it, so a v2
// record can be bound to it before the server has seen it.
const recordIdSchema = idSchema.optional();
// A list's or template's items sealed again on the device for their new
// ids (D329), in the source's order.
const resealedItemsSchema = z
    .array(z.object({ id: idSchema, sealed: sealedSchema }))
    .max(1000)
    .optional();
export const createFamilyLinkSchema = z.union([
    // Encrypted: address, title, preview and note are all inside.
    z.object({
        sealed: sealedSchema,
        messageId: idSchema.optional(),
        id: recordIdSchema,
    }),
    z.object({
        url: z.string().min(1).max(2048),
        note: z.string().max(FAMILY_APP_LIMITS.LINK_NOTE_LENGTH).optional(),
    }),
    z.object({
        messageId: idSchema,
        note: z.string().max(FAMILY_APP_LIMITS.LINK_NOTE_LENGTH).optional(),
    }),
]);
// The shape returned by POST /api/uploads, reused when saving a document.
export const uploadedAttachmentSchema = z.object({
    url: z.string().min(1),
    fileName: z.string().min(1).max(255),
    fileSize: z.number().int().nonnegative(),
    mimeType: z.string().min(1).max(100),
});
export const createFamilyDocumentSchema = z.union([
    z.object({
        sealed: sealedSchema,
        // An encrypted file uploaded for the document, or one already in a chat.
        attachmentUrl: z.string().min(1).max(500).optional(),
        messageId: idSchema.optional(),
        id: recordIdSchema,
    }),
    z.object({
        attachment: uploadedAttachmentSchema,
        description: z
            .string()
            .max(FAMILY_APP_LIMITS.DOCUMENT_DESCRIPTION_LENGTH)
            .optional(),
    }),
    z.object({
        messageId: idSchema,
        description: z
            .string()
            .max(FAMILY_APP_LIMITS.DOCUMENT_DESCRIPTION_LENGTH)
            .optional(),
    }),
]);
export const createFamilyListSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.LIST_NAME_LENGTH)
        .optional(),
    // M28 part 2: the name, encrypted (instead of `name`).
    sealed: sealedSchema.optional(),
    // M26 (D48): start from a saved template.
    templateId: idSchema.optional(),
    id: recordIdSchema,
    items: resealedItemsSchema,
})
    .refine((value) => Boolean(value.name || value.sealed), 'A name is required');
// M26: a calendar date, "2026-10-24".
const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Invalid date');
const listRepeatSchema = z.enum(LIST_REPEATS);
export const saveListTemplateSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.LIST_NAME_LENGTH)
        .optional(),
    sealed: sealedSchema.optional(),
    id: recordIdSchema,
    items: resealedItemsSchema,
});
export const updateFamilyDocumentSchema = z.object({
    description: z
        .string()
        .max(FAMILY_APP_LIMITS.DOCUMENT_DESCRIPTION_LENGTH)
        .nullable()
        .optional(),
    folder: z
        .string()
        .trim()
        .max(FAMILY_APP_LIMITS.DOCUMENT_FOLDER_LENGTH)
        .nullable()
        .optional()
        .transform((value) => (value === '' ? null : value)),
    tags: z
        .array(z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.DOCUMENT_TAG_LENGTH)
        .transform((tag) => tag.toLowerCase().replace(/^#/, '')))
        .max(FAMILY_APP_LIMITS.DOCUMENT_TAGS)
        .optional()
        .transform((tags) => (tags ? [...new Set(tags)] : tags)),
    // M28 part 2: everything above, encrypted, for an encrypted document.
    sealed: sealedSchema.optional(),
});
export const updateFamilyListSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.LIST_NAME_LENGTH)
        .optional(),
    sealed: sealedSchema.optional(),
})
    .refine((value) => Boolean(value.name || value.sealed), 'A name is required');
export const createFamilyListItemSchema = z
    .object({
    text: z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.LIST_ITEM_TEXT_LENGTH)
        .optional(),
    note: z.string().max(FAMILY_APP_LIMITS.LIST_ITEM_NOTE_LENGTH).optional(),
    // M28 part 2: text and note, encrypted (instead of them).
    sealed: sealedSchema.optional(),
    id: recordIdSchema,
    // M26 (D45–D47)
    assignedTo: idSchema.optional(),
    dueDate: isoDateSchema.optional(),
    repeat: listRepeatSchema.optional(),
})
    .refine((value) => Boolean(value.text || value.sealed), 'Text is required');
export const updateFamilyListItemSchema = z.object({
    text: z
        .string()
        .trim()
        .min(1)
        .max(FAMILY_APP_LIMITS.LIST_ITEM_TEXT_LENGTH)
        .optional(),
    note: z
        .string()
        .max(FAMILY_APP_LIMITS.LIST_ITEM_NOTE_LENGTH)
        .nullable()
        .optional(),
    checked: z.boolean().optional(),
    position: z.number().int().nonnegative().optional(),
    // M28 part 2: new text and note, encrypted.
    sealed: sealedSchema.optional(),
    // M26 (D45–D47); null clears.
    assignedTo: idSchema.nullable().optional(),
    dueDate: isoDateSchema.nullable().optional(),
    repeat: listRepeatSchema.nullable().optional(),
});
// ============================================
// Web Push Schemas (M17)
// ============================================
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(2048),
    keys: z.object({
        p256dh: z.string().min(1).max(512),
        auth: z.string().min(1).max(256),
    }),
    userAgent: z.string().max(255).optional(),
});
// A native app's push token (M37, D126).
export const PUSH_PLATFORMS = ['apns', 'apns_voip', 'fcm'];
export const registerPushDeviceSchema = z.object({
    platform: z.enum(PUSH_PLATFORMS),
    token: z.string().min(1).max(4096),
    // APNs only: tokens from development builds work only with the sandbox.
    environment: z.enum(['sandbox', 'production']).optional(),
    // The end-to-end device (D55) this phone registered, so removing the
    // device removes its notifications too.
    deviceKeyId: z.string().uuid().optional(),
    appVersion: z.string().max(40).optional(),
});
export const unsubscribePushSchema = z.object({
    endpoint: z.string().url().max(2048),
});
export const updatePushPreferencesSchema = z.object({
    messages: z.boolean().optional(),
    familyApps: z.boolean().optional(),
    // M24 (D40)
    calls: z.boolean().optional(),
    // M71: the Gatherings switch (the owner's decision, 2026-10-28)
    gatherings: z.boolean().optional(),
    mutedFamilyIds: z
        .array(idSchema)
        .max(PUSH_LIMITS.MAX_MUTED_FAMILIES)
        .optional(),
    mutedConversationIds: z
        .array(idSchema)
        .max(PUSH_LIMITS.MAX_MUTED_CONVERSATIONS)
        .optional(),
    // D233: when a timed mute ends; a muted chat without one is Always.
    mutedConversationUntil: z
        .record(idSchema, z.string().datetime({ offset: true }))
        .refine((value) => Object.keys(value).length <= PUSH_LIMITS.MAX_MUTED_CONVERSATIONS, { message: 'Too many muted chats' })
        .optional(),
});
// ============================================
// Live Call Schemas (M19)
// ============================================
export const callTypeSchema = z.enum([CALL_TYPES.VOICE, CALL_TYPES.VIDEO]);
export const callInitiateSchema = z.object({
    callId: idSchema,
    conversationId: idSchema,
    type: callTypeSchema,
});
export const callIdSchema = z.object({ callId: idSchema });
// M24: mute/camera state (D36) and ringing more of the chat (D38).
export const callMediaSchema = z.object({
    callId: idSchema,
    micEnabled: z.boolean(),
    cameraEnabled: z.boolean(),
});
export const callRingSchema = z.object({
    callId: idSchema,
    userIds: z.array(idSchema).min(1).max(CALL_LIMITS.MAX_PARTICIPANTS),
});
// D278: this device is showing the call ringing.
export const callRingingSchema = z.object({ callId: idSchema });
export const callSignalSchema = z.object({
    callId: idSchema,
    targetUserId: idSchema,
    signal: z.unknown(),
});
// Costs and contributions, published (M43, D109–D111). Amounts in whole rupees.
export const costMonthKeySchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Month as YYYY-MM');
export const costMonthSchema = z.object({
    received: z.number().int().min(0).max(100_000_000),
    // How many families chipped in (D196): a count, never who. Empty = not counted.
    familiesCount: z.number().int().min(0).max(1_000_000).nullable().optional(),
    estimated: z.boolean(),
    note: z.string().trim().max(500).nullable().optional(),
    items: z
        .array(z.object({
        label: z.string().trim().min(1).max(60),
        detail: z.string().trim().max(200).nullable().optional(),
        amount: z.number().int().min(0).max(100_000_000),
        upcoming: z.boolean().default(false),
    }))
        .max(30),
});
// Requesting an invitation (M44, D114–D115).
export const FAMILY_SIZES = ['2-5', '6-10', '11-20', '20+'];
export const inviteRequestSchema = z.object({
    name: z.string().trim().min(1, 'Tell us your name').max(100),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .email('That doesn’t look like an email address')
        .max(254),
    familySize: z.enum(FAMILY_SIZES),
    note: z.string().trim().max(1000).optional().or(z.literal('')),
    // The hidden field bots fill in (D115).
    website: z.string().max(200).optional(),
});
export const updateInviteRequestSchema = z.object({
    status: z.enum(['new', 'invited', 'declined']).optional(),
    note: z.string().trim().max(1000).nullable().optional(),
});
// What you call a family member (D143): Dadi, Nani, Bua… Empty clears it.
export const memberLabelSchema = z.object({
    label: z.string().trim().max(40),
});
// Asking to join a family with its family link (M48, D153). `via` is the
// member whose copy of the link was opened.
export const joinAskSchema = z.object({
    note: z.string().trim().max(JOIN_LIMITS.NOTE_MAX).optional(),
    via: idSchema.optional(),
});
// A new person: the handle must be a signed suggestion, never typed (D147).
export const joinNewAccountSchema = joinAskSchema.extend({
    displayName: z.string().trim().min(1).max(100),
    handle: handleSchema,
    handleToken: z.string().min(1).max(200),
    deviceName: z.string().min(1).max(100).optional(),
});
// Approving a request; only a root may grant root (D1, D154).
export const decideJoinRequestSchema = z.object({
    role: familyRoleSchema.optional(),
});
// Sign in by scanning (M49, D159): the computer names its kind of device.
export const qrDeviceKindSchema = z.enum(QR_DEVICE_KINDS);
export const qrSignInCreateSchema = z.object({
    deviceKind: qrDeviceKindSchema.default('other'),
});
// Shape Twinden (M55, D198–D200). Plain text: a public board, not a chat (D201).
const ideaTitle = z
    .string()
    .trim()
    .min(1, 'Give your idea a title')
    .max(IDEA_LIMITS.TITLE_MAX, `Keep the title to ${IDEA_LIMITS.TITLE_MAX} characters`)
    .refine((title) => /\p{L}|\p{N}/u.test(title), 'Give your idea a title');
const ideaBody = z
    .string()
    .trim()
    .max(IDEA_LIMITS.BODY_MAX, `Keep the details to ${IDEA_LIMITS.BODY_MAX} characters`);
export const ideaSchema = z.object({
    title: ideaTitle,
    body: ideaBody.optional().nullable(),
});
export const ideaEditSchema = z
    .object({ title: ideaTitle.optional(), body: ideaBody.optional().nullable() })
    .refine((edit) => edit.title !== undefined || edit.body !== undefined, {
    message: 'Nothing to change',
});
export const adminIdeaUpdateSchema = z.object({
    status: z.enum(IDEA_STATUSES).optional(),
    note: z.string().trim().max(IDEA_LIMITS.NOTE_MAX).nullable().optional(),
    title: ideaTitle.optional(),
    body: ideaBody.optional().nullable(),
    hidden: z.boolean().optional(),
});
export const ideaMergeSchema = z.object({ targetId: idSchema });
// Report and block (M58, docs/report-block-contact.md D207, D208). Ids only,
// never message content: the server can't read it (M28).
export const reportReasonSchema = z.enum(REPORT_REASONS);
export const createReportSchema = z.object({
    reportedUserId: idSchema,
    reason: reportReasonSchema,
    note: z
        .string()
        .trim()
        .max(REPORT_LIMITS.NOTE_MAX, `Keep the note to ${REPORT_LIMITS.NOTE_MAX} characters`)
        .optional()
        .nullable(),
    conversationId: idSchema.optional().nullable(),
    messageId: idSchema.optional().nullable(),
    /** Also block them (D208). */
    block: z.boolean().optional(),
});
export const markReportSchema = z.object({ handled: z.boolean() });
export const blockUserSchema = z.object({ userId: idSchema });
//# sourceMappingURL=index.js.map